import { Component, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DatePipe } from '@angular/common';
import { MeetingService } from '../../../services/meeting.service';
import { UserMeetingPreferencesService } from '../../../services/user-meeting-preferences.service';
import { UserService } from '../../../services/user.service';
import { Meeting } from '../../../models/meeting';
import {
  TimeSlot,
  BookedPattern,
  slotIsBlocked,
  slotOverlapsBlockedWindow,
  isAtDailyMeetingCap,
  earliestMinutesOn,
  DAY_NAMES,
} from '../../views/student-view/meetings/meeting-request/meeting-request';
import { MeetingPreferences } from '../../../models/meeting-preferences';
import { isDateFullyBlocked, blockedWindowsOn } from '../../../helpers/meeting-preferences-helpers';
import { latestBookableDate, pickerMaxDate } from '../../../helpers/meeting-dates';

export interface ProposeChangesDialogData {
  meeting: Meeting;
}

export type ProposeChangesDialogResult = { action: 'proposed' } | undefined;

/**
 * Lets a counselor propose different terms for a meeting - a new
 * date/time/duration, location, and/or an explanatory message. This is for
 * pending requests or already confirmed meetings.
 * Shares the same `PUT /:id/request-update` endpoint and requestedChanges
 * mechanism the student's own reschedule flow already uses
 * in meeting-details.ts, just from the other side and with more fields
 * on the proposal.
 *
 * The message field is deliberately NOT the meeting's `note` that field is
 * reserved for a counselor's post meeting recap or their reason for denying
 * a fresh request, so it starts blank here rather than pre filled, and is
 * never written back to the meeting even once the proposal is accepted.
 */
@Component({
  selector: 'app-propose-changes-dialog',
  standalone: true,
  providers: [provideNativeDateAdapter(), DatePipe],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    DatePipe,
  ],
  templateUrl: './propose-changes-dialog.html',
  styleUrl: './propose-changes-dialog.css',
})
export class ProposeChangesDialogComponent {
  private fb = inject(FormBuilder);
  private meetingService = inject(MeetingService);
  private prefsService = inject(UserMeetingPreferencesService);
  private userService = inject(UserService);
  protected dialogRef = inject(
    MatDialogRef<ProposeChangesDialogComponent, ProposeChangesDialogResult>,
  );
  protected data = inject<ProposeChangesDialogData>(MAT_DIALOG_DATA);
  protected meeting: Meeting;

  protected submitting = signal(false);
  protected submitError = signal<string | null>(null);

  /** True once the counselor's preferences have loaded (or failed to).
   * The calendar isn't shown before then since `dateFilter` needs them to know
   *  which dates are selectable. Showing it before the preferences because it
   *  made a bug taht greys out every date */
  protected prefsLoaded = signal(false);

  /** Only the counselor sets the actual location/link — a student can
   *  propose switching between remote and in-person, but not which room or
   *  link that resolves to. */
  protected get isCounselor(): boolean {
    return this.userService.currentUser?.id === this.meeting.facultyUserId;
  }

  /** The message field is a counselor only way to explain a proposal to the
   *  student */
  protected get showMessageField(): boolean {
    return this.isCounselor;
  }

  protected readonly earliestDate = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  protected readonly latestDate = latestBookableDate();
  protected readonly pickerMaxDate = pickerMaxDate();

  protected durationForm = this.fb.group({
    durationMinutes: this.fb.control<number | null>(null, Validators.required),
  });
  protected meetingTypeControl = this.fb.control<'remote' | 'in-person'>('in-person');
  protected locationControl = this.fb.control('');
  protected meetingLinkControl = this.fb.control('');
  protected messageControl = this.fb.control('');

  protected allowedDurations = signal<number[]>([]);
  protected selectedDate: Date | null = null;
  protected selectedSlot: Date | null = null;
  protected availableSlots: TimeSlot[] = [];
  protected inDayView = false;

  private preferences = signal<MeetingPreferences | null>(null);
  private meetingCounts: Record<string, number> = {};
  private existingMeetings: Meeting[] = [];

  /** Reflects the format currently selected in the dialog, not necessarily
   *  the meeting's own — the counselor/student can toggle it before submitting. */
  protected get isInPerson(): boolean {
    return this.meetingTypeControl.value === 'in-person';
  }

  constructor() {
    this.meeting = this.data.meeting;
    this.durationForm.controls.durationMinutes.setValue(this.meeting.duration);
    this.meetingTypeControl.setValue(this.meeting.meetingType);
    this.locationControl.setValue(
      this.meeting.meetingType === 'in-person' ? (this.meeting.location ?? '') : '',
    );
    this.meetingLinkControl.setValue(
      this.meeting.meetingType === 'remote' &&
        this.meeting.meetingLink &&
        this.meeting.meetingLink !== 'Link pending'
        ? this.meeting.meetingLink
        : '',
    );
    this.selectedDate = this.meeting.meetingDate;
    this.selectedSlot = this.meeting.start;
    this.loadPreferences();
  }

  private get duration(): number | null {
    return this.durationForm.controls.durationMinutes.value;
  }

  /**
   * Loads the meeting's own preferences and current bookings. */
  private loadPreferences(): void {
    const facultyUserId = this.meeting.facultyUserId;
    const meetingDuration = this.meeting.duration ?? 30;
    this.prefsService.getUserMeetingPreferences(facultyUserId).subscribe({
      next: (prefs) => {
        const digested = MeetingPreferences.digest(prefs);
        this.preferences.set(digested);
        const durations = digested.allowedDurations ?? [];
        this.allowedDurations.set(
          durations.includes(meetingDuration)
            ? durations
            : [...durations, meetingDuration].sort((a, b) => a - b),
        );
        if (this.inDayView && this.selectedDate) {
          this.availableSlots = this.buildSlots(this.selectedDate);
        }
        this.prefsLoaded.set(true);
      },
      error: () => {
        this.preferences.set(null);
        this.allowedDurations.set([meetingDuration]);
        this.prefsLoaded.set(true);
      },
    });
    this.meetingService.getMeetingCountsByDate(facultyUserId).subscribe({
      next: (counts) => (this.meetingCounts = counts),
      error: () => (this.meetingCounts = {}),
    });
    this.meetingService.getMeetings().subscribe({
      next: (meetings) => (this.existingMeetings = meetings),
      error: () => (this.existingMeetings = []),
    });
  }

  protected dateFilter = (date: Date | null): boolean => {
    if (!date) return false;
    const prefs = this.preferences();
    const duration = this.duration;
    if (!prefs || !duration) return false;

    if (isDateFullyBlocked(prefs.blockedAvailabilityIntervals, date)) return false;
    if (isAtDailyMeetingCap(date, this.meetingCounts, prefs.meetingsPerDay)) return false;

    const day = prefs.weeklyAvailability.find(
      (d) => d.enabled && d.day === DAY_NAMES[date.getDay()],
    );
    if (!day) return false;
    if (date > this.latestDate) return false;

    const earliest = earliestMinutesOn(date);
    return day.intervals.some((interval) => {
      if (!interval.start || !interval.end) return false;
      const start = Math.max(
        interval.start.getHours() * 60 + interval.start.getMinutes(),
        earliest,
      );
      const end = interval.end.getHours() * 60 + interval.end.getMinutes();
      return end - start >= duration;
    });
  };

  /**
   * Only the length is changing here, not which date/time the user was
   * looking at. So unlike a new pending request, this never clears the selected
   * date, and tries to carry the same time of day forward onto the new
   * duration rather than dropping the selection entirely.
   */
  protected onDurationChange(): void {
    const previousTimeOfDay = this.selectedSlot
      ? { hours: this.selectedSlot.getHours(), minutes: this.selectedSlot.getMinutes() }
      : null;
    this.selectedSlot = null;

    if (this.selectedDate && previousTimeOfDay) {
      const time = new Date(this.selectedDate);
      time.setHours(previousTimeOfDay.hours, previousTimeOfDay.minutes, 0, 0);
      this.selectedSlot = time;
    }

    if (this.inDayView && this.selectedDate) {
      this.availableSlots = this.buildSlots(this.selectedDate);
    }
  }

  protected onDateSelected(date: Date | null): void {
    this.selectedDate = date;
    this.selectedSlot = null;
    this.inDayView = false;
  }

  protected goToDayView(): void {
    if (!this.selectedDate) return;
    this.availableSlots = this.buildSlots(this.selectedDate);
    this.inDayView = true;
  }

  protected backToMonthView(): void {
    this.inDayView = false;
    this.selectedSlot = null;
  }

  /** Whether two Dates fall on the same calendar day (ignoring time of day). */
  private static isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private buildSlots(date: Date): TimeSlot[] {
    const prefs = this.preferences();
    const duration = this.duration;
    if (!prefs || !duration) return [];

    // Whatever slot is currently selected. The meeting's original time
    // or a time of day carried over across a duration change
    // is always offered and shown as selected on the date it falls on, even
    // if it doesn't land on a step the loop below would generate (the
    // counselor's availability may have changed since the meeting was
    // booked, or a longer/shorter duration shifts the step grid).
    const currentSlot =
      this.selectedSlot && ProposeChangesDialogComponent.isSameDay(date, this.selectedSlot)
        ? this.selectedSlot
        : null;

    const day = prefs.weeklyAvailability.find(
      (d) => d.enabled && d.day === DAY_NAMES[date.getDay()],
    );
    if (!day) return currentSlot ? [{ time: currentSlot, blocked: false }] : [];
    const dow = date.getDay();

    const sessionPatterns: BookedPattern[] = this.existingMeetings
      .filter((m) => {
        if (!m.start || m.id === this.meeting.id) return false;
        if (m.status === 'canceled' || m.status === 'declined') return false;
        return (
          m.start.getFullYear() === date.getFullYear() &&
          m.start.getMonth() === date.getMonth() &&
          m.start.getDate() === date.getDate()
        );
      })
      .map((m) => {
        const dur = m.end ? Math.round((m.end.getTime() - m.start!.getTime()) / 60_000) : duration;
        return {
          dayOfWeek: dow,
          startHour: m.start!.getHours(),
          startMinute: m.start!.getMinutes(),
          durationMinutes: dur,
        };
      });

    const blockedWindows = blockedWindowsOn(prefs.blockedAvailabilityIntervals, date);
    const earliest = earliestMinutesOn(date);

    const slots: TimeSlot[] = [];
    for (const interval of day.intervals) {
      if (!interval.start || !interval.end) continue;
      const startMinutes = interval.start.getHours() * 60 + interval.start.getMinutes();
      const endMinutes = interval.end.getHours() * 60 + interval.end.getMinutes();
      for (let m = startMinutes; m + duration <= endMinutes; m += duration) {
        if (m < earliest) continue;
        const h = Math.floor(m / 60);
        const min = m % 60;
        const time = new Date(date);
        time.setHours(h, min, 0, 0);
        slots.push({
          time,
          blocked:
            slotIsBlocked(h, min, duration, sessionPatterns, dow) ||
            slotOverlapsBlockedWindow(m, m + duration, blockedWindows),
        });
      }
    }

    if (currentSlot && !slots.some((s) => s.time.getTime() === currentSlot.getTime())) {
      slots.push({ time: currentSlot, blocked: false });
      slots.sort((a, b) => a.time.getTime() - b.time.getTime());
    }

    return slots;
  }

  protected selectSlot(slot: TimeSlot): void {
    if (!slot.blocked) this.selectedSlot = slot.time;
  }

  /** Formats a Date as a local "YYYY-MM-DD" string (no UTC conversion). */
  private static toDateString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  /** Formats a Date as a local "HH:MM:SS" string (no UTC conversion). */
  private static toTimeString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }

  /**
   * What to send for location/meetingLink, given the chosen format. Only the
   * counselor can set the actual room or link — a student switching format
   * (or leaving it as-is) always carries forward whatever the counselor last
   * set, falling back to a sensible default only when there's nothing to
   * carry forward (the format itself changed away from the meeting's own).
   */
  private resolveLocationFields(): { location: string; meetingLink: string | null } {
    const meetingType = this.meetingTypeControl.value;
    const sameType = meetingType === this.meeting.meetingType;

    if (meetingType === 'in-person') {
      const location = this.isCounselor
        ? this.locationControl.value?.trim() || 'TBD'
        : sameType
          ? (this.meeting.location ?? 'TBD')
          : 'TBD';
      return { location, meetingLink: null };
    }

    const meetingLink = this.isCounselor
      ? this.meetingLinkControl.value?.trim() || 'Link pending'
      : sameType
        ? (this.meeting.meetingLink ?? 'Link pending')
        : 'Link pending';
    return { location: '', meetingLink };
  }

  protected submit(): void {
    const duration = this.duration;
    if (!this.selectedSlot || !duration) return;

    this.submitting.set(true);
    this.submitError.set(null);

    const changes: {
      meetingDate: string;
      startTime: string;
      duration: number;
      location: string;
      meetingLink: string | null;
      message?: string;
    } = {
      meetingDate: ProposeChangesDialogComponent.toDateString(this.selectedSlot),
      startTime: ProposeChangesDialogComponent.toTimeString(this.selectedSlot),
      duration,
      ...this.resolveLocationFields(),
    };
    const message = this.messageControl.value?.trim();
    if (message) changes.message = message;

    this.meetingService.proposeChanges(this.meeting.id, changes).subscribe({
      next: () => {
        this.submitting.set(false);
        this.dialogRef.close({ action: 'proposed' });
      },
      error: (err: HttpErrorResponse) => {
        this.submitting.set(false);
        this.submitError.set(
          err.error?.error || 'Something went wrong sending your proposal. Please try again.',
        );
      },
    });
  }
}
